import { z } from 'zod';
/**
 * Program rule templates — DATA-MODEL.md §3, ARCHITECTURE.md §7.
 *
 * Four templates, a closed discriminated union, no expression language and no DSL.
 * That is a deliberate constraint: a rules engine is untestable in the way that
 * matters, and every template here is one strategy class with its own test suite.
 *
 * Adding a fifth means a schema, an `EarnStrategy`, an enum migration and full
 * coverage — never a config tweak.
 *
 * All four have a strategy, and all four can be created and edited from the
 * dashboard (features/PROGRAMS.md).
 */
export const PROGRAM_TYPES = ['STAMPS', 'POINTS', 'SPEND_THRESHOLD', 'TIERS'];
/**
 * The largest transaction amount the ledger can hold.
 *
 * `LoyaltyEvent.delta` and `Enrollment.cachedBalance` are `int4`, capped at
 * 2 147 483 647, and a spend-threshold delta *is* the amount — so an unbounded
 * `amountCents` is a Postgres `22003` inside the stamping transaction and a 500
 * at the counter rather than a clean refusal.
 *
 * Ten million minor units: $100 000, or a very large restaurant bill in a
 * currency with small units. Chosen so a full threshold cycle plus a few visits
 * still fits comfortably inside `int4` rather than sitting just under it.
 */
export const MAX_AMOUNT_CENTS = 10_000_000;
export const stampsRulesSchema = z.strictObject({
    type: z.literal('STAMPS'),
    /** Stamps needed for the reward. */
    threshold: z.number().int().min(2).max(50),
    rewardId: z.uuid(),
    maxPerDay: z.number().int().min(1).max(10).default(1),
    /** Blocks a barista stamping the same customer repeatedly in one visit. */
    cooldownMinutes: z.number().int().min(0).max(1440).default(30),
});
/**
 * Scale for the points rate — the reason it is an integer.
 *
 * `pointsPerCurrencyUnit` was a float, and floats cannot hold the rates
 * merchants actually pick. `(amountCents / 100) * 1.15` on a $100 purchase is
 * `114.99999999999999`, which floors to 114 — off by one, on an ordinary
 * configuration, written permanently into an append-only ledger where the only
 * correction is a compensating event.
 *
 * A thousandth of a point per currency unit is finer than any merchant will
 * ever configure and keeps every intermediate value an exact integer, so the
 * class of error disappears rather than being rounded away. `1.15` points per
 * dollar is `1150`.
 */
export const POINTS_RATE_SCALE = 1000;
export const pointsRulesSchema = z.strictObject({
    type: z.literal('POINTS'),
    /**
     * Points per currency unit, in thousandths — see `POINTS_RATE_SCALE`.
     *
     * Integer, deliberately: the whole computation stays in integers, so a rate a
     * merchant can express is a rate the ledger can hold exactly.
     */
    pointsPerUnitMilli: z.number().int().positive().max(1_000_000),
    rounding: z.enum(['FLOOR', 'ROUND', 'CEIL']).default('FLOOR'),
    minAmountCents: z.number().int().min(0).default(0),
    rewardIds: z.array(z.uuid()).min(1),
    /**
     * Most points-earning transactions in a day, and the wait between them.
     *
     * Present for the same reason `STAMPS` has them, and more urgently: a stamp
     * card's abuse ceiling is one stamp per tap, and a points card's is whatever
     * amount a barista types. Without these, a double-tap that mints a new
     * idempotency key earns twice for one transaction.
     */
    maxPerDay: z.number().int().min(1).max(50).default(10),
    cooldownMinutes: z.number().int().min(0).max(1440).default(1),
});
export const spendThresholdRulesSchema = z.strictObject({
    type: z.literal('SPEND_THRESHOLD'),
    /**
     * Spend needed for the reward, in minor units.
     *
     * Bounded by `MAX_AMOUNT_CENTS` for the same reason as an individual amount:
     * `LoyaltyEvent.delta` and `Enrollment.cachedBalance` are `int4`, and a
     * threshold that cannot be reached without overflowing them is a programme
     * that breaks at the counter rather than at configuration time.
     */
    thresholdCents: z.number().int().positive().max(MAX_AMOUNT_CENTS),
    rewardId: z.uuid(),
    /**
     * `null` means spend accumulates forever, which is the only value this
     * version accepts.
     *
     * A window is a real feature and it is not built: nothing reads this field,
     * so a programme configured with one would accumulate forever anyway while
     * appearing to expire. Refused at creation rather than silently ignored — the
     * same treatment `TIERS` gets, and for the same reason: a merchant should
     * find out when they configure it, not when a customer asks why their spend
     * from last year still counts.
     */
    windowDays: z.null().default(null),
    /** See the points template — a points-style ceiling on an amount-based earn. */
    maxPerDay: z.number().int().min(1).max(50).default(10),
    cooldownMinutes: z.number().int().min(0).max(1440).default(1),
});
export const tiersRulesSchema = z.strictObject({
    type: z.literal('TIERS'),
    /**
     * Thousandths of a point per currency unit, like `POINTS`.
     *
     * This field was `pointsPerCurrencyUnit: z.number().positive()` — a float, and
     * the identical bug the points template shipped and had to have fixed:
     * `(10000 / 100) * 1.15` is `114.99999999999999`, which floors to 114 and is
     * written permanently to an append-only ledger.
     *
     * It went unnoticed here because nothing implemented `TIERS`, so nothing ever
     * multiplied by it. Corrected before the strategy exists rather than after —
     * the same defect twice in one schema file would be hard to explain.
     */
    pointsPerUnitMilli: z.number().int().positive().max(1_000_000),
    rounding: z.enum(['FLOOR', 'ROUND', 'CEIL']).default('FLOOR'),
    minAmountCents: z.number().int().min(0).default(0),
    tiers: z
        .array(z.strictObject({
        name: z.string().min(1).max(40),
        minPoints: z.number().int().min(0),
        perks: z.array(z.string()).default([]),
    }))
        .min(2)
        /**
         * Ascending, with no repeated threshold.
         *
         * Two tiers at the same `minPoints` makes "which tier is this customer"
         * ambiguous, and the answer would depend on array order — a merchant
         * reordering a list in a form would silently move customers between tiers.
         */
        .refine((tiers) => tiers.every((t, i) => i === 0 || t.minPoints > tiers[i - 1].minPoints), { message: 'Tiers must be listed in ascending order, with no two at the same points' })
        /**
         * The first tier starts at zero.
         *
         * Otherwise a new customer belongs to no tier at all, and every surface has
         * to render an absence — "your tier: none" is worse than a starting rung.
         */
        .refine((tiers) => tiers[0]?.minPoints === 0, {
        message: 'The lowest tier must start at 0 points',
    }),
    /** Points are counted over this window, not for ever — see `TiersStrategy`. */
    evaluationWindowDays: z.number().int().min(30).max(730).default(365),
    /**
     * How long somebody keeps a tier after falling below it.
     *
     * features/OVERVIEW.md: "Downgrade handling with grace: warn before demotion, never demote
     * silently." Zero would be a silent demotion by another name.
     */
    demotionGraceDays: z.number().int().min(1).max(180).default(30),
    maxPerDay: z.number().int().min(1).max(50).default(10),
    cooldownMinutes: z.number().int().min(0).max(1440).default(1),
});
export const programRulesSchema = z.discriminatedUnion('type', [
    stampsRulesSchema,
    pointsRulesSchema,
    spendThresholdRulesSchema,
    tiersRulesSchema,
]);
export const EXPIRY_POLICIES = ['NONE', 'FIXED_DAYS', 'ROLLING_INACTIVITY'];
/**
 * Expiry is always explicit. A policy other than NONE requires a day count,
 * because a silent balance decrement is exactly what AGENTS.md §4.7 forbids.
 */
export const expirySchema = z
    .object({
    expiryPolicy: z.enum(EXPIRY_POLICIES).default('NONE'),
    expiryDays: z.number().int().min(1).max(3650).nullable().default(null),
})
    .refine((v) => v.expiryPolicy === 'NONE' || v.expiryDays !== null, {
    message: 'expiryDays is required unless the expiry policy is NONE',
    path: ['expiryDays'],
});
/**
 * The rewards a programme offers, by id.
 *
 * One definition, because two things need it and they must agree: creation
 * checks the ids belong to the brand, and redemption checks the reward being
 * spent on is one the programme actually names. Before redemption checked, a
 * points programme's `rewardIds` was a field a merchant configured and nothing
 * read — any active reward in the brand was redeemable against points,
 * including ones deliberately left off the programme.
 *
 * TIERS names none: its perks are text, not `Reward` rows.
 */
export function programRewardIds(rules) {
    switch (rules.type) {
        case 'STAMPS':
        case 'SPEND_THRESHOLD':
            return [rules.rewardId];
        case 'POINTS':
            return rules.rewardIds;
        case 'TIERS':
            return [];
    }
}
/**
 * Points for an amount, in integers — the arithmetic itself.
 *
 * Shared rather than server-only because the counter needs the same answer
 * offline: an optimistic result that guesses at a different number than the
 * ledger will record is a progress line that jumps when the queue drains, and
 * two implementations of this sum would drift the first time either changed.
 *
 * `amountCents × pointsPerUnitMilli` is exact. Dividing by
 * `100 × POINTS_RATE_SCALE` converts minor units to currency units and undoes
 * the rate's scale in one step, and that division is the only place rounding
 * happens — the merchant's `rounding` choice rather than an artefact of a float.
 */
export function pointsForAmount(
// The rate fields only: `TIERS` earns with the same arithmetic as `POINTS`.
rules, amountCents) {
    const numerator = amountCents * rules.pointsPerUnitMilli;
    const denominator = 100 * POINTS_RATE_SCALE;
    switch (rules.rounding) {
        case 'CEIL':
            return Math.ceil(numerator / denominator);
        case 'ROUND':
            // Integer half-up, so the result does not depend on a float being
            // representable — `Math.round` on a quotient reintroduces exactly the
            // problem the integer rate removes.
            return Math.floor((numerator + denominator / 2) / denominator);
        case 'FLOOR':
        default:
            return Math.floor(numerator / denominator);
    }
}
/**
 * What a visit of this size is worth, before any cap or cooldown.
 *
 * The limits are the server's business — a client that enforced them would be a
 * client deciding what a merchant gives away — but the *units* are the same on
 * both sides, and the counter needs them to render an optimistic result offline.
 */
export function earnUnitsFor(rules, amountCents, quantity = 1) {
    switch (rules.type) {
        case 'STAMPS':
            return quantity;
        case 'POINTS':
            return amountCents === null ? 0 : pointsForAmount(rules, amountCents);
        case 'SPEND_THRESHOLD':
            return amountCents === null ? 0 : amountCents;
        case 'TIERS':
            // Earns exactly as `POINTS` does — `TiersStrategy` calls the same sum.
            return amountCents === null ? 0 : pointsForAmount(rules, amountCents);
    }
}
/**
 * What a reward costs on a given programme.
 *
 * On a threshold template the price of the reward *is* the card: an eight-stamp
 * programme's reward costs eight stamps, and a "spend 100" programme's costs
 * 10 000 minor units. `Reward.costUnits` is ignored there.
 *
 * That is not a preference, it is the removal of a defect class. The two numbers
 * were independent: an eight-stamp programme naming a reward priced at 3 showed
 * the customer "3 of 8" progress toward a reward they could already redeem, and
 * `redeem` charged them 3. Deriving the cost means the two cannot disagree,
 * rather than being validated into agreement at creation and drifting the first
 * time somebody edits the reward.
 *
 * A points programme has no threshold, so its rewards keep their own prices —
 * that is the whole point of the template.
 */
export function rewardCostUnits(rules, reward) {
    switch (rules.type) {
        case 'STAMPS':
            return rules.threshold;
        case 'SPEND_THRESHOLD':
            return rules.thresholdCents;
        case 'POINTS':
        case 'TIERS':
            // Both spend points against rewards priced individually.
            return reward.costUnits;
    }
}
/**
 * The tier a points total earns — features/PROGRAMS.md.
 *
 * The highest rung whose threshold has been reached. Shared rather than
 * server-only because the customer card renders it and the dashboard previews
 * it, and a tier computed two ways is a tier a customer will eventually be told
 * twice, differently.
 *
 * The schema guarantees the list is ascending and starts at zero, so this always
 * returns a tier — there is no "no tier" state for anybody enrolled.
 */
export function tierFor(rules, points) {
    let current = rules.tiers[0];
    for (const tier of rules.tiers) {
        if (points >= tier.minPoints)
            current = tier;
        else
            break;
    }
    return current;
}
/** The next rung up and what it costs to reach, or null at the top. */
export function nextTier(rules, points) {
    const next = rules.tiers.find((t) => t.minPoints > points);
    return next ? { tier: next, pointsAway: next.minPoints - points } : null;
}
// ── What a balance is counting ──────────────────────────────
/**
 * The unit behind a balance and a threshold.
 *
 * Crosses the API boundary, so it lives here rather than in the API's strategy
 * layer (AGENTS.md §1). Before it existed, every surface guessed from the
 * program type: `lookup`, the customer card and the merchant's customer list
 * all derived the threshold as `type === 'STAMPS' ? threshold : null`, so a
 * customer who had spent $105 toward a $100 reward saw "10500" with no progress
 * and no reward moment, and their history read "4500 stamps".
 */
export const THRESHOLD_UNITS = ['STAMPS', 'POINTS', 'MINOR_CURRENCY'];
/**
 * The units needed for the reward, or null on a template that has no single
 * target. One place, because the field is named differently per template and
 * every surface that guessed picked `threshold` and got `undefined`.
 */
export function programThreshold(rules) {
    switch (rules.type) {
        case 'STAMPS':
            return rules.threshold;
        case 'SPEND_THRESHOLD':
            return rules.thresholdCents;
        case 'POINTS':
        case 'TIERS':
            return null;
    }
}
/**
 * The minutes a card must wait between earns.
 *
 * Every template carries a cooldown — the amount-based ones got one when they
 * were built, because their abuse ceiling is whatever number a barista types
 * rather than a single stamp. Anything reading it off `rules`
 * directly has to narrow the union first, and the fraud report did that with
 * `rules.type === 'STAMPS' ? rules.cooldownMinutes : 0` — which silently
 * disabled its cooldown-gaming heuristic on exactly the two templates where
 * gaming pays best.
 */
export function programCooldownMinutes(rules) {
    switch (rules.type) {
        case 'STAMPS':
        case 'POINTS':
        case 'SPEND_THRESHOLD':
        case 'TIERS':
            return rules.cooldownMinutes;
    }
}
/** Which template counts in which unit. One place, so no surface has to guess. */
export const THRESHOLD_UNIT_BY_TYPE = {
    STAMPS: 'STAMPS',
    POINTS: 'POINTS',
    SPEND_THRESHOLD: 'MINOR_CURRENCY',
    TIERS: 'POINTS',
};
/**
 * The templates a merchant can choose, with the words the dashboard uses.
 *
 * Here rather than in the dashboard because the set is defined by which
 * strategies exist, and a chooser that offers a template the server refuses is
 * a merchant filling in a form to be told no at the end of it.
 */
export const SELECTABLE_TEMPLATES = [
    {
        type: 'STAMPS',
        name: 'Stamp card',
        summary: 'A stamp per visit. Ten coffees, one free.',
        atSetup: true,
    },
    {
        type: 'POINTS',
        name: 'Points',
        summary: 'Points for what they spend, redeemable against rewards of different sizes.',
        atSetup: true,
    },
    {
        type: 'SPEND_THRESHOLD',
        name: 'Spend target',
        summary: 'One reward when their total spend crosses an amount you set.',
        atSetup: true,
    },
    {
        type: 'TIERS',
        name: 'Tiers',
        summary: 'Silver, Gold and so on, earned by what they spend over a year.',
        /**
         * Not offered during signup.
         *
         * A tier programme needs a ladder — three rungs, each with a threshold —
         * and asking for that at step four of a wizard is how somebody abandons
         * onboarding. The other three take one number. Tiers are started from the
         * programme editor instead, where there is room to think about them.
         */
        atSetup: false,
    },
];
/** The templates simple enough to choose during signup — see `atSetup`. */
export const SETUP_TEMPLATES = SELECTABLE_TEMPLATES.filter((t) => t.atSetup);
/** Whether the counter must ask for a transaction amount before it can earn. */
export function requiresAmount(unit) {
    return unit !== 'STAMPS';
}
/**
 * A quantity of whatever this programme counts in, as a phrase.
 *
 * For copy like "this uses 8 stamps" — which the counter's redemption screen
 * hard-coded as stamps, so a points customer was told that spending 120 points
 * "uses 120 stamps".
 *
 * No currency symbol on the money case, for the reason given on
 * `formatMinorUnits`: the caller knows the currency, this does not.
 */
export function formatUnits(units, thresholdUnit) {
    switch (thresholdUnit) {
        case 'STAMPS':
            return `${units} stamp${units === 1 ? '' : 's'}`;
        case 'POINTS':
            return `${units} point${units === 1 ? '' : 's'}`;
        case 'MINOR_CURRENCY':
            return formatMinorUnits(units);
    }
}
/**
 * The progress line a customer reads, formatted once for both sides.
 *
 * Shared because the counter has to produce it offline — an optimistic stamp
 * never reaches a strategy — and two implementations of the same sentence drift
 * the moment one template changes. The offline label was `${balance} points`
 * for everything that was not a stamp card, which called a spend balance in
 * minor units "10500 points".
 *
 * Progress is capped at the threshold. A customer at 9 on an 8-stamp card is one
 * stamp into their next card, and "9 of 8" makes the counter look broken.
 *
 * No currency symbol: the currency lives on the Location, and AGENTS.md §10 forbids
 * hard-coding one. The caller adds the symbol it knows about.
 */
export function formatProgress({ balance, threshold, thresholdUnit }) {
    if (thresholdUnit === 'POINTS' || threshold === null) {
        return `${balance} point${balance === 1 ? '' : 's'}`;
    }
    const shown = Math.min(Math.max(0, balance), threshold);
    if (thresholdUnit === 'MINOR_CURRENCY') {
        return `${formatMinorUnits(shown)} of ${formatMinorUnits(threshold)}`;
    }
    return `${shown} of ${threshold}`;
}
/**
 * Minor units as a decimal amount, with no symbol.
 *
 * The 100 is minor units per currency unit, and every currency SignaPass serves
 * is two-decimal. A zero- or three-decimal currency would need a change here and
 * in every amount field, so this is the honest simplification rather than a
 * hidden assumption.
 */
export function formatMinorUnits(cents) {
    return (cents / 100).toFixed(2);
}
//# sourceMappingURL=program-rules.js.map